import React, { useEffect, useMemo, useState } from "react";
import {
  Controller,
  Resolver,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, CircularProgress } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { InnerProps } from "../dynamicFormTypes";
import useFormulaRunner from "../../../hooks/useFormulaRunner";
import { resolveUploads } from "../../../utils/forms.utils";
import { uploadFormsFile } from "../../../api/forms.api";
import { Score, GetDecisionInForm } from "../../widgets";
import Weather from "../../widgets/Score/Weather/Weather";
import FileField from "./formFields/FileField";
import FormFieldLinks from "./FormFieldLinks";
import IframeRenderer from "../../IframeRenderer/IframeRenderer";
import Slider from "../../slider/Slider";
import SliderButtons from "../../slider/SliderButtons";
import "./DynamicFormSkin5.scss";
const getInputType = (type: string) => {
  if (type === "encrypted" || type === "password") {
    return "password";
  }
  if (type === "email") {
    return "email";
  }
  if (type === "number") {
    return "number";
  }
  return "text";
};
const popupValuesToText = (values: Record<string, any>) => {
  return Object.values(values)
    .filter((value) => value !== "" && value !== null && value !== undefined)
    .map((value) => (typeof value === "string" ? value.trim() : String(value)))
    .filter(Boolean)
    .join(" | ");
};
const getPlaceholder = (field: any) => {
  if (field.placeholder) {
    return field.placeholder;
  }
  if (field.type === "email") {
    return "you@example.com";
  }
  if (field.type === "encrypted" || field.type === "password") {
    return "Enter password";
  }
  return field.label ? `Enter ${String(field.label).toLowerCase()}` : "";
};
const isInputField = (type: string) =>
  type === "text" ||
  type === "email" ||
  type === "number" ||
  type === "password" ||
  type === "encrypted" ||
  type === "string";
const isEnumeratedField = (field: any) =>
  field.type === "select" ||
  field.type === "radio" ||
  field.type === "enumerated";
const hasValue = (value: any) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
};
const isOptionalField = (field: any) =>
  String(field.label || "")
    .toLowerCase()
    .includes("optional");
const shouldValidateField = (field: any) =>
  isInputField(String(field.type)) ||
  isEnumeratedField(field) ||
  field.type === "date" ||
  field.type === "video" ||
  field.type === "image" ||
  field.type === "document";
const DynamicFormSkin5: React.FC<InnerProps> = ({
  formId,
  formName,
  fields,
  schema,
  defaults,
  onSubmit,
  onValuesChange,
  apiSubmitting,
  saveButtonLabel = "Save",
  hasCancelButton = false,
  hasResetButton = false,
  onCancel,
  insetLabel,
  cancelLabel,
  formTitle,
}) => {
  const [visiblePasswords, setVisiblePasswords] = useState<{
    [key: string]: boolean;
  }>({});
  const [missingRequiredFields, setMissingRequiredFields] = useState<
    Set<string>
  >(new Set());
  const skin5Resolver = useMemo<Resolver<Record<string, any>>>(() => {
    const resolver = zodResolver(schema) as Resolver<Record<string, any>>;
    return async (values, context, options) => {
      const result = await resolver(values, context, options);
      const filteredErrors: Record<string, any> = {
        ...result.errors,
      };
      fields.forEach((field) => {
        if (isOptionalField(field) && !hasValue(values[field.id])) {
          delete filteredErrors[field.id];
        }
      });
      if (Object.keys(filteredErrors).length) {
        return {
          values: {},
          errors: filteredErrors,
        };
      }
      return {
        values,
        errors: {},
      };
    };
  }, [schema, fields]);
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Record<string, any>>({
    resolver: skin5Resolver,
    defaultValues: defaults,
    mode: "onBlur",
  });
  const formulaRunner = useFormulaRunner();
  const watched = useWatch({ control });
  useEffect(() => {
    fields.forEach((field) => {
      const booleanTargetFieldId = (field as any).booleanTargetFieldId;
      if (field.typeBehavior !== "multiple" || !booleanTargetFieldId) {
        return;
      }
      const targetField = fields.find(
        (item) => item.id === booleanTargetFieldId,
      );
      if (!targetField) {
        return;
      }
      const multipleValue = watched?.[field.id];
      const hasSelection = Array.isArray(multipleValue)
        ? multipleValue.length > 0
        : multipleValue !== null &&
          multipleValue !== undefined &&
          multipleValue !== "";
      const targetOption = (targetField.options || []).find((option) => {
        const value = String(option.value ?? "").trim().toLowerCase();
        return hasSelection
          ? value === "true" || value === "yes"
          : value === "false" || value === "no";
      });
      if (!targetOption) {
        return;
      }
      if (watched?.[targetField.id] === targetOption.value) {
        return;
      }
      setValue(targetField.id, targetOption.value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    });
  }, [fields, watched, setValue]);
  useEffect(() => {
    fields.forEach((field) => {
      if (field.typeBehavior !== "multiple") {
        return;
      }
      const current = getValues(field.id);
      if (Array.isArray(current)) {
        return;
      }
      const precheckedValues = (field.options || [])
        .filter((option) => option.checkedInQuestions)
        .map((option) => option.value);
      setValue(field.id, precheckedValues, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }, [fields, getValues, setValue]);
  const visibleFields = useMemo(
    () => fields.filter((field) => !field.hidden),
    [fields],
  );
  const formulaFields = useMemo(() => {
    const result: {
      id: string;
      formula: string;
    }[] = [];
    fields.forEach((field) => {
      if (field.calculated && field.formula) {
        result.push({
          id: field.id,
          formula: field.formula,
        });
      }
    });
    return result;
  }, [fields]);
  useEffect(() => {
    onValuesChange(watched || {});
  }, [watched, onValuesChange]);
  useEffect(() => {
    const values = (watched || {}) as Record<string, any>;
    setMissingRequiredFields((previous) => {
      if (!previous.size) {
        return previous;
      }
      const next = new Set(previous);
      let changed = false;
      previous.forEach((fieldId) => {
        if (hasValue(values[fieldId])) {
          next.delete(fieldId);
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [watched]);
  const getFieldPoints = (id: string, optionValue: any): number => {
    if (!id) {
      return 0;
    }
    const foundField = fields.find((field) => field.id === id);
    if (!foundField || !foundField.options?.length) {
      return 0;
    }
    const selectedOption = foundField.options.find(
      (option) => option.value === optionValue,
    );
    if (!selectedOption || !selectedOption.point) {
      return 0;
    }
    return parseFloat(selectedOption.point);
  };
  const getFormulaResult = (formula: string): string => {
    const data = getValues();
    const mergedData: Record<string, { value: any; points: number }> = {};
    Object.keys(data).forEach((key) => {
      const value = data[key];
      mergedData[key] = {
        value,
        points: getFieldPoints(key, value),
      };
    });
    return formulaRunner(formula, mergedData) as string;
  };
  const resetForm = () => {
    const oldValuesKeys = Object.keys(getValues());
    const newValues: Record<string, string> = {};
    oldValuesKeys.forEach((key) => {
      newValues[key] = "";
    });
    reset(newValues);
    setMissingRequiredFields(new Set());
  };
  const checkRequiredFields = (values: Record<string, any>) => {
    const missingFields = visibleFields
      .filter(
        (field) =>
          shouldValidateField(field) &&
          !isOptionalField(field) &&
          !hasValue(values[field.id]),
      )
      .map((field) => field.id);
    setMissingRequiredFields(new Set(missingFields));
    return missingFields.length === 0;
  };
  const submit: SubmitHandler<Record<string, any>> = async (vals) => {
    if (!checkRequiredFields(vals)) {
      return;
    }
    const formulaValues: Record<string, string> = {};
    formulaFields.forEach((formulaField) => {
      formulaValues[formulaField.id] = getFormulaResult(formulaField.formula);
    });
    const uploadedFileValues = await resolveUploads(
      formId,
      fields,
      vals,
      uploadFormsFile,
    );
    await onSubmit({
      ...vals,
      ...uploadedFileValues,
      ...formulaValues,
    });
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          event: "DYNAMIC_FORM_SKIN_5_SAVED",
          formId,
        },
        "*",
      );
    }
  };
  const togglePasswordVisibility = (fieldId: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  };
  if (!visibleFields.length) {
    return null;
  }
  return (
    <div className="dynamic-form-skin5">
      {(formTitle || formName) && (
        <div className="dynamic-form-skin5__header">
          <h2 className="dynamic-form-skin5__title">{formTitle || formName}</h2>
        </div>
      )}
      <form
        className="dynamic-form-skin5__form"
        onSubmit={handleSubmit(
          submit,
          () => checkRequiredFields(getValues()),
        )}
        noValidate
      >
        <div className="dynamic-form-skin5__fields">
          {visibleFields.map((f) => {
            if (
              f.type === "video" ||
              f.type === "image" ||
              f.type === "document"
            ) {
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => (
                    <div className="dynamic-form-skin5__field">
                      <FileField
                        value={(field.value as File) ?? null}
                        label={f.label}
                        error={
                          missingRequiredFields.has(f.id)
                            ? "Please fill out this"
                            : (errors[f.id]?.message as string)
                        }
                        onChange={field.onChange}
                        src={f.src}
                        links={f.links}
                      />
                    </div>
                  )}
                />
              );
            }
            if (f.type === "widget") {
              if (f.typeBehavior === "sliderBar") {
                let score = "0";
                const sourceField = fields.find(
                  (field) => field.id === f.source,
                );
                if (sourceField) {
                  if (sourceField.formula) {
                    score = getFormulaResult(sourceField.formula);
                  } else {
                    const sourceValue = getValues()[f.source] as string;
                    score = sourceValue ?? "0";
                  }
                }
                return (
                  <div key={f.id} className="dynamic-form-skin5__field">
                    <Score score={score} />
                  </div>
                );
              }
              if (f.typeBehavior === "weather") {
                let city = "";
                const sourceField = fields.find(
                  (field) => field.id === f.source,
                );
                if (sourceField) {
                  if (sourceField.formula) {
                    city = getFormulaResult(sourceField.formula);
                  } else {
                    const sourceValue = getValues()[f.source] as string;
                    city = sourceValue ?? "";
                  }
                }
                if (!city) {
                  return null;
                }
                return (
                  <div key={f.id} className="dynamic-form-skin5__field">
                    <Weather city={city} />
                  </div>
                );
              }
              if (f.typeBehavior === "getdecisioninform") {
                return (
                  <div key={f.id} className="dynamic-form-skin5__field">
                    <GetDecisionInForm />
                  </div>
                );
              }
            }
            if (f.type === "number" && f.typeBehavior === "slider") {
              const fieldConfig = f as any;
              const options =
                fieldConfig.options &&
                !Array.isArray(fieldConfig.options) &&
                typeof fieldConfig.options === "object"
                  ? fieldConfig.options
                  : {};
              const min = Number(options.min ?? fieldConfig.min ?? 0);
              const max = Number(options.max ?? fieldConfig.max ?? 10);
              const step = Number(options.step ?? fieldConfig.step ?? 1);
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => {
                    const hasRequiredError = missingRequiredFields.has(f.id);
                    const sliderValue = hasValue(field.value)
                      ? field.value
                      : fieldConfig.defaultValue ?? fieldConfig.value ?? min;
                    return (
                      <div className="dynamic-form-skin5__field">
                        <div className="dynamic-form-skin5__label-row">
                          <div className="dynamic-form-skin5__label">
                            {f.label}
                            {f.links && f.links.length > 0 && (
                              <FormFieldLinks links={f.links} />
                            )}
                          </div>
                          <span className="dynamic-form-skin5__value">
                            {sliderValue}
                          </span>
                        </div>
                        <div
                          className={`dynamic-form-skin5__slider ${hasRequiredError ? "dynamic-form-skin5__slider--error" : ""}`}
                        >
                          <Slider
                            hideThumbButtons
                            min={min}
                            max={max}
                            step={step}
                            sx={{ width: "100%" }}
                            size="small"
                            defaultValue={fieldConfig.defaultValue ?? fieldConfig.value ?? min}
                            value={sliderValue}
                            onChangeCommitted={(_, value) => field.onChange(value)}
                            valueLabelDisplay="auto"
                            leftText={String(min)}
                            rightText={String(max)}
                          />
                        </div>
                        {(hasRequiredError || !!errors[f.id]) && (
                          <div className="dynamic-form-skin5__error">
                            {hasRequiredError
                              ? "Please fill out this"
                              : (errors[f.id]?.message as string)}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              );
            }
            if (f.type === "number" && f.typeBehavior === "radio") {
              const fieldConfig = f as any;
              const options =
                fieldConfig.options &&
                !Array.isArray(fieldConfig.options) &&
                typeof fieldConfig.options === "object"
                  ? fieldConfig.options
                  : {};
              const min = Number(options.min ?? fieldConfig.min ?? 0);
              const max = Number(options.max ?? fieldConfig.max ?? 10);
              const step = Number(options.step ?? fieldConfig.step ?? 1);
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => {
                    const hasRequiredError = missingRequiredFields.has(f.id);
                    return (
                      <div className="dynamic-form-skin5__field">
                        <div className="dynamic-form-skin5__label-row">
                          <div className="dynamic-form-skin5__label">
                            {f.label}
                            {f.links && f.links.length > 0 && (
                              <FormFieldLinks links={f.links} />
                            )}
                          </div>
                        </div>
                        <div className="dynamic-form-skin5__slider-buttons">
                          <SliderButtons
                            min={min}
                            max={max}
                            step={step}
                            value={field.value as any}
                            error={hasRequiredError || !!errors[f.id]}
                            onChange={field.onChange}
                          />
                        </div>
                        {(hasRequiredError || !!errors[f.id]) && (
                          <div className="dynamic-form-skin5__error">
                            {hasRequiredError
                              ? "Please fill out this"
                              : (errors[f.id]?.message as string)}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              );
            }
            if (isInputField(String(f.type))) {
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => {
                    const inputType = getInputType(String(f.type));
                    const isPassword = inputType === "password";
                    const isPasswordVisible = !!visiblePasswords[f.id];
                    const finalInputType =
                      isPassword && isPasswordVisible ? "text" : inputType;
                    const hasRequiredError = missingRequiredFields.has(f.id);
                    return (
                      <div className="dynamic-form-skin5__field">
                        <div className="dynamic-form-skin5__label-row">
                          <label
                            className="dynamic-form-skin5__label"
                            htmlFor={f.id}
                          >
                            {f.label}
                            {f.links && f.links.length > 0 && (
                                <FormFieldLinks
                                    links={f.links}
                                    onPopupSaved={({ values }) => {
                                        field.onChange(popupValuesToText(values));
                                    }}
                                />
                            )}
                          </label>
                        </div>
                        <div className="dynamic-form-skin5__input-wrap">
                          <input
                            id={f.id}
                            className={`
                                                            dynamic-form-skin5__input
                                                            ${isPassword ? "dynamic-form-skin5__input--password" : ""}
                                                        `}
                            type={finalInputType}
                            style={{
                              borderColor: hasRequiredError
                                ? "#d32f2f"
                                : undefined,
                            }}
                            value={
                              (field.value ?? "") as
                                | string
                                | number
                                | readonly string[]
                                | undefined
                            }
                            placeholder={getPlaceholder(f)}
                            onChange={(event) => {
                              if (f.type === "number") {
                                field.onChange(
                                  event.target.value === ""
                                    ? ""
                                    : Number(event.target.value),
                                );
                                return;
                              }
                              field.onChange(event);
                            }}
                            onBlur={field.onBlur}
                          />
                          {isPassword && (
                            <button
                              className="dynamic-form-skin5__password-button"
                              type="button"
                              aria-label={
                                isPasswordVisible
                                  ? "Hide password"
                                  : "Show password"
                              }
                              onClick={() => togglePasswordVisibility(f.id)}
                            >
                              {isPasswordVisible ? (
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M2.5 12C3.7 9.1 7 5 12 5C17 5 20.3 9.1 21.5 12C20.3 14.9 17 19 12 19C7 19 3.7 14.9 2.5 12Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M12 15A3 3 0 1 0 12 9A3 3 0 0 0 12 15Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M3 3L21 21"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M10.6 10.6A2 2 0 0 0 13.4 13.4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M9.9 5.1A9.8 9.8 0 0 1 12 4.9C17 4.9 20.3 9.1 21.5 12C21 13.1 20.3 14.3 19.3 15.3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M6.6 6.6C4.6 7.9 3.2 10.1 2.5 12C3.7 14.9 7 19.1 12 19.1C13.4 19.1 14.7 18.8 15.8 18.3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                        {(hasRequiredError || !!errors[f.id]) && (
                          <div className="dynamic-form-skin5__error">
                            {hasRequiredError
                              ? "Please fill out this"
                              : (errors[f.id]?.message as string)}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              );
            }
            if (f.type === "url") {
              if (f.typeBehavior === "displaycontent") {
                return (
                  <div
                    key={f.id}
                    className="dynamic-form-skin5__field dynamic-form-skin5__iframe"
                  >
                    <IframeRenderer src={f.label} />
                  </div>
                );
              }
              return (
                <div key={f.id} className="dynamic-form-skin5__field">
                  <button
                    type="button"
                    className="dynamic-form-skin5__url"
                    onClick={() => window.open(f.label)}
                  >
                    {f.label}
                  </button>
                </div>
              );
            }
            if (f.type === "color") {
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => (
                    <div className="dynamic-form-skin5__field">
                      <div className="dynamic-form-skin5__label-row">
                        <div className="dynamic-form-skin5__label">
                          {f.label}
                          {f.links && f.links.length > 0 && (
                            <FormFieldLinks links={f.links} />
                          )}
                        </div>
                        <span className="dynamic-form-skin5__color-value">
                          {field.value || "#000000"}
                        </span>
                      </div>
                      <div className="dynamic-form-skin5__color-wrap">
                        <input
                          id={f.id}
                          className="dynamic-form-skin5__color-input"
                          type="color"
                          value={field.value || "#000000"}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      </div>
                      {!!errors[f.id] && (
                        <div className="dynamic-form-skin5__error">
                          {errors[f.id]?.message as string}
                        </div>
                      )}
                    </div>
                  )}
                />
              );
            }
            if (f.type === "date") {
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => (
                    <div className="dynamic-form-skin5__field">
                      <div className="dynamic-form-skin5__label-row">
                        <div className="dynamic-form-skin5__label">
                          {f.label}
                          {f.links && f.links.length > 0 && (
                            <FormFieldLinks links={f.links} />
                        )}
                        </div>
                      </div>
                      <DatePicker
                        className="dynamic-form-skin5__date-picker"
                        minDate={dayjs().startOf("day")}
                        value={
                          field.value
                            ? dayjs(
                                field.value as string | number | Date | Dayjs,
                              )
                            : null
                        }
                        onChange={(v) => {
                          if (!v || !dayjs(v).isValid()) {
                            field.onChange(null);
                            return;
                          }
                          field.onChange(dayjs(v).toDate().toISOString());
                        }}
                        slotProps={{
                          textField: {
                            error:
                              missingRequiredFields.has(f.id) || !!errors[f.id],
                            helperText: missingRequiredFields.has(f.id)
                              ? "Please fill out this"
                              : (errors[f.id]?.message as string) || "",
                            fullWidth: true,
                          },
                          openPickerButton: {
                            sx: {
                              color: "#677e77",
                              "&:hover": {
                                backgroundColor: "rgba(74, 150, 123, 0.08)",
                              },
                            },
                          },
                          desktopPaper: {
                            sx: {
                              "& .MuiPickersDay-root.Mui-selected": {
                                backgroundColor: "#4a967b",
                              },
                              "& .MuiPickersDay-root.Mui-selected:hover": {
                                backgroundColor: "#3f856c",
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                />
              );
            }
            if (isEnumeratedField(f)) {
              return (
                <Controller
                  key={f.id}
                  name={f.id}
                  control={control}
                  render={({ field }) => {
                    const isMultiple = f.typeBehavior === "multiple";
                    const currentValues = Array.isArray(field.value)
                      ? field.value
                      : [];
                    const singleValue = field.value;
                    return (
                      <div className="dynamic-form-skin5__field">
                        <div className="dynamic-form-skin5__section-label">
                          {f.label}
                          {f.links && f.links.length > 0 && (
                            <FormFieldLinks links={f.links} />
                          )}
                        </div>
                        <div className="dynamic-form-skin5__enum-group">
                          {(f.options || []).map((option) => {
                            const optionDescription =
                              (option as any).description ||
                              (option as any).subtitle ||
                              "";
                            const isSelected = isMultiple
                              ? currentValues.includes(option.value)
                              : singleValue === option.value;
                            return (
                              <button
                                key={String(option.value)}
                                type="button"
                                className={`
                                                                    dynamic-form-skin5__enum-option
                                                                    ${isSelected ? "dynamic-form-skin5__enum-option--selected" : ""}
                                                                `}
                                style={{
                                  borderColor: missingRequiredFields.has(f.id)
                                    ? "#d32f2f"
                                    : undefined,
                                }}
                                onClick={() => {
                                  if (isMultiple) {
                                    const nextValue = isSelected
                                      ? currentValues.filter(
                                          (item: any) => item !== option.value,
                                        )
                                      : [...currentValues, option.value];
                                    field.onChange(nextValue);
                                    return;
                                  }
                                  field.onChange(option.value);
                                }}
                              >
                                <span className="dynamic-form-skin5__enum-title">
                                  {option.label}
                                </span>
                                {optionDescription && (
                                  <span className="dynamic-form-skin5__enum-description">
                                    {optionDescription}
                                  </span>
                                )}
                                {option.links && option.links.length > 0 && (
                                  <FormFieldLinks links={option.links} />
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {(missingRequiredFields.has(f.id) ||
                          !!errors[f.id]) && (
                          <div className="dynamic-form-skin5__error">
                            {missingRequiredFields.has(f.id)
                              ? "Please fill out this"
                              : (errors[f.id]?.message as string)}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              );
            }
            if (f.type === "formula") {
              return (
                <div key={f.id} className="dynamic-form-skin5__field">
                  <div className="dynamic-form-skin5__label">
                    {f.label}
                    {f.links && f.links.length > 0 && (
                      <FormFieldLinks links={f.links} />
                    )}
                  </div>
                  <div className="dynamic-form-skin5__formula-box">
                    {getFormulaResult(f.formula)}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
        {hasCancelButton ? (
          <div className="dynamic-form-skin5__actions">
            <Button
              variant="outlined"
              disabled={isSubmitting || apiSubmitting}
              onClick={onCancel}
              sx={{
                width: "49%",
                borderColor: "#4a967b",
                color: "#4a967b",
                borderRadius: "12px",
                textTransform: "none",
              }}
            >
              {cancelLabel || "Back"}
            </Button>
            <Button
              startIcon={
                isSubmitting || apiSubmitting ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
              type="submit"
              variant="contained"
              disabled={isSubmitting || apiSubmitting}
              sx={{
                width: "49%",
                backgroundColor: "#4a967b",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "#3f856c",
                },
              }}
            >
              {insetLabel || saveButtonLabel}
            </Button>
          </div>
        ) : hasResetButton ? (
          <div className="dynamic-form-skin5__actions">
            <Button
              variant="outlined"
              disabled={isSubmitting || apiSubmitting}
              onClick={resetForm}
              sx={{
                width: "49%",
                borderColor: "#4a967b",
                color: "#4a967b",
                borderRadius: "12px",
                textTransform: "none",
              }}
            >
              {cancelLabel || "Reset"}
            </Button>
            <Button
              startIcon={
                isSubmitting || apiSubmitting ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
              type="submit"
              variant="contained"
              disabled={isSubmitting || apiSubmitting}
              sx={{
                width: "49%",
                backgroundColor: "#4a967b",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "#3f856c",
                },
              }}
            >
              {insetLabel || saveButtonLabel}
            </Button>
          </div>
        ) : (
          <div className="dynamic-form-skin5__actions dynamic-form-skin5__actions--single">
            <Button
              startIcon={
                isSubmitting || apiSubmitting ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
              type="submit"
              variant="contained"
              disabled={isSubmitting || apiSubmitting}
              sx={{
                width: "100%",
                height: "52px",
                backgroundColor: "#4a967b",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "16px",
                boxShadow: "0 8px 18px rgba(74, 150, 123, 0.28)",
                "&:hover": {
                  backgroundColor: "#3f856c",
                },
              }}
            >
              {insetLabel || saveButtonLabel}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};
export default DynamicFormSkin5;